import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, hr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { CalendarIcon, Plus, Trash2, Send, Loader2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { format, eachDayOfInterval, isWeekend } from "date-fns";
import { hr as hrLocale } from "date-fns/locale";
import { isHoliday, isCollectiveDay, holidayName, setCollectivePeriod, setHolidays } from "../lib/holidays";

// Iter49: COLL_START/END su sada dinamični (default fallback prije nego /vacation/info učita)
const DEFAULT_COLL_START = new Date("2026-07-27");
const DEFAULT_COLL_END = new Date("2026-08-14");
const CALENDAR_YEAR_START = 2026;
const CALENDAR_YEAR_END = 2027;

function workdaysBetween(s, e, opts) {
  if (!s || !e || e < s) return 0;
  const excludeCollective = !!(opts && opts.excludeCollective);
  return eachDayOfInterval({start: s, end: e}).filter(d => {
    if (isWeekend(d) || isHoliday(d)) return false;
    if (excludeCollective && isCollectiveDay(d)) return false;
    return true;
  }).length;
}

function formatDateRange(from, to, emptyLabel) {
  if (!from) return emptyLabel;
  if (!to) return format(from, "dd.MM.yyyy");
  return `${format(from, "dd.MM.yyyy")} – ${format(to, "dd.MM.yyyy")}`;
}

export default function NewRequest() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [fund, setFund] = useState(null); // Iter41: { fond, used_decided, used_pending, available }
  // Iter38: acceptsCollective starts as null. User can click "Prihvaćam navedeno"
  // to acknowledge it visually, but submitting works regardless — backend always
  // gets accepts_collective=true (the reject path was removed per user request).
  // Iter48: kad je `info.collective_enabled === false`, kolektivna kartica se NE prikazuje
  // i acceptsCollective ostaje null (nema kolektivnih dana u zahtjevu).
  const [acceptsCollective, setAcceptsCollective] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  useEffect(() => {
    api.get("/vacation/info").then(r => {
      setInfo(r.data);
      // Iter49: ažuriraj globalni isCollectiveDay() helper s razdobljem iz Postavki
      if (r.data?.collective_start && r.data?.collective_end) {
        setCollectivePeriod(r.data.collective_start, r.data.collective_end);
      }
      // Iter50: ažuriraj popis praznika (bazni 2026 + admin-dodani extras)
      if (Array.isArray(r.data?.holidays) && r.data.holidays.length > 0) {
        setHolidays(r.data.holidays);
      }
    });
  }, []);
  useEffect(() => { api.get("/vacation/my-fund").then(r => setFund(r.data)).catch(() => {}); }, []);

  // Iter38: defensive setter — date-fns range mode can return `undefined` on
  // re-click after a complete selection, which crashed `format(range.from, ...)`
  // and caused a blank screen requiring F5. Always coerce to a safe shape.
  const safeSetRange = (r) => {
    if (!r) { setRange({ from: undefined, to: undefined }); return; }
    setRange({ from: r.from || undefined, to: r.to || undefined });
  };

  const addPeriod = () => {
    if (!range.from || !range.to) { toast.error("Odaberite početak i kraj perioda"); return; }
    const newPeriod = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      start: format(range.from, "yyyy-MM-dd"),
      end: format(range.to, "yyyy-MM-dd"),
    };
    setPeriods([...periods, newPeriod]);
    setRange({ from: undefined, to: undefined });
  };

  const removePeriod = (id) => setPeriods(periods.filter((p) => p.id !== id));

  const reorder = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...periods];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setPeriods(next);
  };

  const handleDragStart = (e, i) => { setDragIdx(i); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver = (e, i) => { e.preventDefault(); setDragOverIdx(i); };
  const handleDrop = (e, i) => { e.preventDefault(); reorder(dragIdx, i); setDragIdx(null); setDragOverIdx(null); };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  // Iter48: kad postavka isključi obavezno prihvaćanje → kartica i collDays ispadaju.
  // Iter57e: ako je zaposlenik osobno oslobođen kolektivnog (npr. znanstvenik
  // u radu tijekom kolektivnog), tretira se kao da je collective isključen ZA NJEGA.
  // Iter100: ako je kolektivni već iskorišten u tekućoj godini (već ima rješenje),
  // sakri karticu i tretiraj kao da nije potrebno prihvaćanje — dopusti dodatne pojedinačne dane.
  const exemptFromCollective = !!fund?.exempt_from_collective;
  const collectiveAlreadyUsed = !!fund?.collective_already_used;
  const collectiveEnabled = (info ? info.collective_enabled !== false : true) && !exemptFromCollective && !collectiveAlreadyUsed;
  const collDays = (collectiveEnabled && acceptsCollective) ? (info?.collective_workdays ?? 0) : 0;
  // Iter96/Iter100: dedupe dodatnih dana protiv kolektivnog kad ga se prihvaća SADA
  // ILI je već iskorišten kroz prethodno rješenje.
  const excludeCollFromAdd = (collectiveEnabled && !!acceptsCollective) || collectiveAlreadyUsed;
  const addDays = periods.reduce((s, p) => s + workdaysBetween(new Date(p.start), new Date(p.end), { excludeCollective: excludeCollFromAdd }), 0);
  const totalDays = collDays + addDays;
  const hasAnyDays = totalDays > 0;
  // Iter57c: kad je obavezno prihvaćanje kolektivnog UKLJUČENO, korisnik mora
  // OBAVEZNO kliknuti "Prihvaćam navedeno razdoblje" prije slanja — neovisno
  // o tome ima li dodatne periode. Prije se moglo predati samo additional_periods
  // bez prihvaćanja, ali to je krivo: poslodavac (KIF) odlučuje da je kolektivni
  // odmor obavezan, pa svaki zahtjev mora započeti prihvaćanjem.
  const mustAcceptCollective = collectiveEnabled && !acceptsCollective;
  // Iter41: provjera fonda — totalDays ne smije premašiti available (preostalo).
  // Iter70: rezervacija za novi kolektivni smanjuje raspoloživo za nove zahtjeve.
  const availForReq = fund ? (fund.available_for_request ?? fund.available) : 0;
  const exceedsFund = fund ? totalDays > availForReq : false;

  const submit = async () => {
    if (mustAcceptCollective) {
      toast.error("Morate prihvatiti razdoblje kolektivnog godišnjeg odmora prije slanja zahtjeva. Klikni 'Prihvaćam navedeno razdoblje' iznad kalendara.");
      return;
    }
    if (!hasAnyDays) {
      toast.error("Odaberi barem jedan dan godišnjeg — ili klikni 'Prihvaćam navedeno razdoblje', ili dodaj period kroz kalendar.");
      return;
    }
    if (exceedsFund) {
      toast.error(`Premašujete fond godišnjeg odmora — preostalo: ${availForReq} dana, traženo: ${totalDays}`);
      return;
    }
    setBusy(true);
    try {
      const payload = periods.map(({ start, end }) => ({ start, end }));
      // Iter38b: accepts_collective reflects ACTUAL user choice (no auto-include)
      // Iter48: kad je kolektivni isključen u postavkama, šalji uvijek false.
      await api.post("/vacation/requests", {
        additional_periods: payload, note,
        accepts_collective: collectiveEnabled ? !!acceptsCollective : false,
      });
      toast.success("Zahtjev predan voditelju na suglasnost");
      nav("/zahtjevi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="new-request-page">
      <div>
        <p className="text-sm text-muted-foreground uppercase tracking-wider">Novi zahtjev</p>
        <h1 className="serif text-4xl font-semibold">Predaja zahtjeva za godišnji odmor</h1>
      </div>

      {exemptFromCollective && (
        <Card className="border-emerald-300 bg-emerald-50/50" data-testid="exempt-from-collective-card">
          <CardContent className="p-4 flex items-start gap-3">
            <span className="text-lg leading-none mt-0.5">ℹ️</span>
            <div>
              <div className="font-medium text-emerald-900">Oslobođeni ste kolektivnog godišnjeg odmora</div>
              <div className="text-xs text-emerald-800 mt-1">
                Tajništvo Vas je označilo kao izuzetog iz kolektivnog razdoblja. Zahtjev možete predati samo s vlastitim periodima — nije potrebno prihvaćanje kolektivnog.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {collectiveAlreadyUsed && !exemptFromCollective && (
        <Card className="border-blue-300 bg-blue-50/50" data-testid="collective-already-used-card">
          <CardContent className="p-4 flex items-start gap-3">
            <span className="text-lg leading-none mt-0.5">✓</span>
            <div>
              <div className="font-medium text-blue-900">Kolektivni godišnji odmor je već iskorišten u {fund?.current_year}. godini</div>
              <div className="text-xs text-blue-800 mt-1">
                Već imate izdato rješenje s prihvaćenim kolektivnim godišnjim odmorom. Za dodatne pojedinačne dane
                odaberite periode u kalendaru — nije potrebno ponovno prihvaćati kolektivni. Datumi unutar kolektivnog
                razdoblja su u kalendaru onemogućeni jer su već uključeni u prethodno rješenje.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {collectiveEnabled && (
      <Card className="border-accent/30 bg-accent/5" data-testid="collective-card">
        <CardHeader><CardTitle className="text-base">Obvezni kolektivni godišnji odmor</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm leading-relaxed">{info?.message}</p>
          <div className="mt-3 flex items-center gap-2 text-sm">
            <Badge variant="secondary" data-testid="collective-range-badge">
              {info ? `${info.collective_start?.split("-").reverse().join(".")}. – ${info.collective_end?.split("-").reverse().join(".")}.` : "—"}
            </Badge>
            <span className="text-muted-foreground">
              {info?.collective_workdays ?? "—"} radnih dana
              {info?.return_to_work && <> · povratak na posao {info.return_to_work.split("-").reverse().join(".")}.</>}
            </span>
          </div>
          {acceptsCollective === null && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => setAcceptsCollective(true)} data-testid="accept-collective-btn">Prihvaćam navedeno razdoblje</Button>
            </div>
          )}
          {acceptsCollective === true && (
            <div className="mt-4 text-sm flex items-center gap-3">
              <Badge>Prihvaćeno — uključeno u zahtjev</Badge>
              <button className="text-xs text-muted-foreground underline hover:text-foreground" onClick={() => setAcceptsCollective(null)} data-testid="reset-collective-btn">
                Poništi (ne uključuj kolektivni)
              </button>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Dodatni dani godišnjeg odmora (opcionalno)</CardTitle>
          <p className="text-xs text-muted-foreground">Odaberite periode izvan kolektivnog odmora.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {mustAcceptCollective && (
            <div
              className="flex items-start gap-3 p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm"
              data-testid="must-accept-collective-warning"
            >
              <span className="text-base leading-none mt-0.5">⚠️</span>
              <div>
                <div className="font-medium">Prvo prihvatite kolektivni godišnji odmor.</div>
                <div className="text-xs mt-0.5 opacity-90">
                  Dok ne kliknete &quot;Prihvaćam navedeno razdoblje&quot; iznad, ne možete predati zahtjev — niti dodatne periode, niti samo kolektivni.
                </div>
              </div>
            </div>
          )}
          {periods.length > 0 && (
            <div className="space-y-2">
              {periods.length > 1 && (
                <p className="text-[11px] text-muted-foreground italic">
                  💡 Povucite za promjenu redoslijeda perioda
                </p>
              )}
              {periods.map((p, i) => (
                <div
                  key={p.id}
                  draggable={periods.length > 1}
                  onDragStart={(e) => handleDragStart(e, i)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDrop={(e) => handleDrop(e, i)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center justify-between bg-secondary/60 rounded-md px-3 py-2 transition-all
                    ${dragIdx === i ? "opacity-40" : ""}
                    ${dragOverIdx === i && dragIdx !== i ? "ring-2 ring-accent" : ""}
                    ${periods.length > 1 ? "cursor-move" : ""}`}
                  data-testid={`period-${i}`}
                >
                  <div className="flex items-center gap-2">
                    {periods.length > 1 && (
                      <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" data-testid={`drag-handle-${i}`}/>
                    )}
                    <div className="text-sm">
                      <span className="text-[11px] text-muted-foreground mr-2 font-mono">#{i + 1}</span>
                      <span className="font-medium">{hr(p.start)} – {hr(p.end)}</span>
                      {(() => {
                        const raw = workdaysBetween(new Date(p.start), new Date(p.end));
                        const eff = workdaysBetween(new Date(p.start), new Date(p.end), { excludeCollective: excludeCollFromAdd });
                        const insideColl = raw - eff;
                        return (
                          <>
                            <span className="text-muted-foreground ml-2">({eff} radnih dana)</span>
                            {excludeCollFromAdd && insideColl > 0 && (
                              <span className="text-[11px] text-blue-700 ml-2" data-testid={`period-coll-overlap-${i}`}>
                                — {insideColl} dan{insideColl === 1 ? "" : "a"} unutar kolektivnog (ne broji se)
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removePeriod(p.id)} data-testid={`remove-period-${i}`}>
                    <Trash2 className="w-4 h-4"/>
                  </Button>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="pick-range-btn">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {formatDateRange(range.from, range.to, "Odaberi period")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="range" selected={range} onSelect={safeSetRange} numberOfMonths={2} locale={hrLocale}
                  disabled={(d) => excludeCollFromAdd && isCollectiveDay(d)}
                  dayTooltip={(d) => {
                    if (excludeCollFromAdd && isCollectiveDay(d)) return "Unutar kolektivnog GO — automatski uključeno u zahtjev";
                    if (isHoliday(d)) return "Blagdan (ne troši fond)";
                    if (isCollectiveDay(d)) return "Unutar razdoblja kolektivnog GO";
                    return "";
                  }}
                  modifiers={{ holiday: isHoliday, collective: isCollectiveDay }}
                  modifiersClassNames={{
                    holiday: "bg-red-100 text-red-700 font-semibold",
                    collective: "bg-blue-100 text-blue-700",
                  }}
                  fromYear={CALENDAR_YEAR_START} toYear={CALENDAR_YEAR_END} defaultMonth={new Date("2026-06-01")} />
              </PopoverContent>
            </Popover>
            <Button onClick={addPeriod} variant="secondary" data-testid="add-period-btn">
              <Plus className="w-4 h-4 mr-2" /> Dodaj period
            </Button>
            <div className="text-xs text-muted-foreground flex items-center gap-3 ml-auto">
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-100 border border-blue-300"/> Kolektivni odmor</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-100 border border-red-300"/> Blagdan (ne troši fond)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Napomena (neobvezno)</CardTitle></CardHeader>
        <CardContent>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Npr. razlog dodatnih dana, kontakt zamjene…" data-testid="note-input" />
        </CardContent>
      </Card>

      <Card className={exceedsFund ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground"}>
        <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider opacity-70">Sažetak</div>
            <div className="serif text-2xl mt-1">
              {collectiveEnabled
                ? <>Kolektivni: {collDays} + Dodatno: {addDays} = </>
                : <>Dodatni dani: </>}
              <span className={exceedsFund ? "" : "text-accent"}>{collDays + addDays} radnih dana</span>
            </div>
            <div className="text-xs opacity-70 mt-1">Zahtjev se šalje voditelju: {user.manager_email || "—"}</div>
            {fund && (
              <div className="text-xs opacity-90 mt-2 space-y-1" data-testid="fund-breakdown">
                <div>
                  Fond {fund.current_year}: <b>{fund.fond}</b> · Iskorišteno: <b>{fund.used_decided}</b>
                  {(fund.baseline_current ?? 0) > 0 && (
                    <span className="opacity-75"> (baseline {fund.baseline_current} + zahtjevi {fund.auto_current})</span>
                  )}
                  {fund.used_pending > 0 && <> · Rezervirano (pending): <b>{fund.used_pending}</b></>}
                </div>
                {fund.prev_year_fond !== null && fund.prev_year_fond !== undefined && (
                  <div>
                    {fund.prev_carryover_expired
                      ? <span className="opacity-60 line-through">Prijenos {fund.prev_year}: {fund.prev_carryover_total} dana — istekao 30.06.</span>
                      : <>Prijenos {fund.prev_year}: <b>{fund.prev_carryover_available}</b> od <b>{fund.prev_year_fond}</b> dana (iskorišteno {fund.prev_year_iskoristeno}, vrijedi do {fund.prev_carryover_expires?.split("-").reverse().join(".")}.)</>}
                  </div>
                )}
                <div className="pt-1 border-t border-primary-foreground/20">
                  Preostalo ukupno (stari + novi): <b>{fund.available}</b> dana
                  {(fund.collective_reserve ?? 0) > 0 && (
                    <div className="text-[11px] mt-0.5" data-testid="collective-reserve-info">
                      Rezervirano za novi kolektivni GO: <b>{fund.collective_reserve}</b> dana ·
                      Dostupno za ovaj zahtjev: <b>{fund.available_for_request}</b> dana
                    </div>
                  )}
                </div>
                {/* Iter55: alocacija novog zahtjeva — prvo stari fond, pa novi */}
                {totalDays > 0 && !exceedsFund && (
                  <div className="text-[11px] opacity-85 mt-1">
                    Za ovaj zahtjev ({totalDays} dana): {(() => {
                      const fromOld = Math.min(totalDays, fund.prev_carryover_available || 0);
                      const fromNew = totalDays - fromOld;
                      const parts = [];
                      if (fromOld > 0) parts.push(`${fromOld} dana iz prijenosa ${fund.prev_year}.`);
                      if (fromNew > 0) parts.push(`${fromNew} dana iz fonda ${fund.current_year}.`);
                      return parts.join(" + ") || "—";
                    })()}
                  </div>
                )}
              </div>
            )}
            {mustAcceptCollective && (
              <div className="text-sm font-semibold mt-2" data-testid="must-accept-warning">
                ⚠ Morate prihvatiti razdoblje kolektivnog godišnjeg odmora (ili dodati barem jedan vlastiti period).
              </div>
            )}
            {exceedsFund && (
              <div className="text-sm font-semibold mt-2" data-testid="exceeds-fund-warning">
                ⚠ Premašujete fond za {totalDays - availForReq} dana — smanjite zahtjev ili kontaktirajte tajništvo.
              </div>
            )}
          </div>
          <Button size="lg" variant="secondary" onClick={submit} disabled={busy || mustAcceptCollective || !hasAnyDays || exceedsFund} data-testid="submit-request-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Send className="w-4 h-4 mr-2"/>}
            {mustAcceptCollective ? "Prvo prihvati kolektivni" : (!hasAnyDays ? "Prvo odaberi dane" : (exceedsFund ? "Premašuje fond" : "Predaj zahtjev"))}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
